import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import spotifyService from '../services/spotifyService';
import LayoutHeader from '../layout/LayoutHeader';

const SpotifyCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');

        if (error) {
          setError(`Spotify authorization failed: ${error}`);
          setStatus('error');
          return;
        }

        if (!code || !state) {
          setError('Missing authorization code or state parameter');
          setStatus('error');
          return;
        }

        setStatus('exchanging');
        
        // Exchange code for access token
        await spotifyService.exchangeCodeForToken(code, state);
        
        setStatus('success');
        
        // Redirect back to playlisting app after a short delay
        setTimeout(() => {
          navigate('/playlisting');
        }, 2000);

      } catch (err) {
        console.error('Error handling Spotify callback:', err);
        setError(err.message || 'Failed to complete Spotify authentication');
        setStatus('error');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const handleRetry = () => {
    navigate('/playlisting');
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'processing':
        return 'Processing Spotify authorization...';
      case 'exchanging':
        return 'Completing authentication...';
      case 'success':
        return 'Successfully connected to Spotify! Redirecting...';
      case 'error':
        return 'Authentication failed';
      default:
        return 'Processing...';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
      case 'exchanging':
        return (
          <div className="w-8 h-8 border-4 border-[#5856D6] border-t-transparent rounded-full animate-spin"></div>
        );
      case 'success':
        return (
          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
          
          {/* Status Display */}
          <div className="text-center">
            <div className="flex justify-center mb-6">
              {getStatusIcon()}
            </div>
            
            <h2 className="text-white text-[18px] font-semibold mb-4">
              Spotify Authentication
            </h2>
            
            <p className="text-[rgba(255,255,255,0.7)] text-[14px] mb-6">
              {getStatusMessage()}
            </p>

            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <p className="text-red-400 text-[12px]">{error}</p>
              </div>
            )}

            {status === 'error' && (
              <button
                onClick={handleRetry}
                className="bg-[#5856D6] text-white px-6 py-3 rounded-lg text-[14px] hover:bg-[#5856D6CC] transition-colors"
              >
                Try Again
              </button>
            )}

            {status === 'success' && (
              <div className="flex items-center justify-center gap-2 text-green-400 text-[12px]">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                Redirecting to Playlisting...
              </div>
            )}
          </div>

        </div>
      </div>
    </LayoutHeader>
  );
};

export default SpotifyCallback; 